import React from 'react'
import { Link } from '@inertiajs/react'
import PortfolioLayout from '@/layouts/PortfolioLayout'
import PortfolioHero from '@/components/portfolio/PortfolioHero'
import PortfolioKpiStrip from '@/components/portfolio/PortfolioKpiStrip'
import ActionCenter from '@/components/portfolio/ActionCenter'
import PropertyCard from '@/components/portfolio/PropertyCard'
import ForwardHeatmap from '@/components/portfolio/ForwardHeatmap'
import RevparSmallMultiples from '@/components/portfolio/RevparSmallMultiples'
import { Plus } from 'lucide-react'
import { HelpTooltip } from '@/components/HelpTooltip'
import { useBrand } from '@/hooks/useBrand'
import type { AccountPropertyGroup } from '@/types/shared'

interface SparklinePoint {
  date: string
  occ: number
}

// EVERY ROW ON THE BRIEFING CARRIES ITS OWN PATH. The portfolio has no property in scope, so
// there is no page-level basePath to hang a row link off; `basePath` is served per row by
// Operator::PortfoliosController. Append fixed suffixes only — never rebuild the URL from `id`.
interface PropertyCardData {
  id: number
  name: string
  /** This hotel's canonical path — the card is a link to it. */
  basePath: string
  shortname: string
  currency: string
  totalRooms: number
  bookedTonight: number
  occupancyPct: number
  adr: number
  revpar: number
  stlyOccupancyPct: number
  stlyAdr: number
  stlyRevpar: number
  stlyHasData: boolean
  sparkline: SparklinePoint[]
  /** Confirmed bookings created in the trailing 7 days — a count, not pickup. See PropertyCard. */
  newBookings7d: number
  syncStatus: {
    lastStructureSync: string | null
    lastComputation: string | null
    lastPush: string | null
    lastPushCount: number | null
    errorCode: string | null
    errorMessage: string | null
    errorAt: string | null
  }
}

interface PortfolioKpis {
  occupancyPct: number
  occupancyPctStly: number
  adr: number
  adrStly: number
  revpar: number
  revparStly: number
  mtdRevenue: number
  mtdRevenueStly: number
  next30Revenue: number
  next30RevenueStly: number
  currency: string
  totalRooms: number
  activeProperties: number
}

interface ActionItem {
  severity: 'error' | 'warning' | 'info'
  title: string
  message: string
  propertyId: number
  propertyName: string
  /** The canonical path of the hotel this item is about. */
  propertyBasePath: string
  age: string | null
}

interface HeatmapData {
  days: string[]
  rows: {
    propertyId: number
    propertyName: string
    /** The canonical path of this row's hotel — its name is a link into it. */
    propertyBasePath: string
    cells: { date: string; occ: number; booked: number; capacity: number }[]
  }[]
}

interface RevparTrend {
  propertyId: number
  propertyName: string
  /** The canonical path of this small multiple's hotel — its title is a link into it. */
  propertyBasePath: string
  currency: string
  points: { date: string; revpar: number }[]
}

interface PortfolioProps {
  currentUser: { id: number; email: string }
  // tenancy-14: account-grouped shape (shared with TopBar/AccountSwitcher). Optional +
  // defaulted so a briefly-absent prop during the concurrent backend rollout never crashes.
  properties?: AccountPropertyGroup[]
  today: string
  empty: boolean
  portfolioKpis?: PortfolioKpis
  propertyCards?: PropertyCardData[]
  actionItems?: ActionItem[]
  forwardHeatmap?: HeatmapData
  revparTrends?: RevparTrend[]
}

export default function Portfolio({
  currentUser,
  properties = [],
  today,
  empty,
  portfolioKpis,
  propertyCards,
  actionItems,
  forwardHeatmap,
  revparTrends,
}: PortfolioProps) {
  const brand = useBrand()

  if (empty) {
    return (
      <PortfolioLayout title="Portfolio" currentUser={currentUser} properties={properties}>
        <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            Welcome to {brand}
          </div>
          <h1 className="mt-4 flex max-w-2xl items-center justify-center gap-2 text-3xl font-light tracking-tight text-foreground sm:text-5xl">
            <span>
              Let&apos;s connect your <span className="italic font-medium text-primary">first property</span>.
            </span>
            <HelpTooltip content={`${brand} reads your property structure and bookings from your PMS, computes optimised rates, and publishes them back — start by connecting your first property.`} />
          </h1>
          <p className="mt-4 max-w-md text-sm text-muted-foreground">
            {brand} reads from your PMS and computes optimised rates across your portfolio. Begin by adding a property.
          </p>
          <Link
            href="/onboarding"
            className="mt-8 inline-flex items-center gap-2 rounded-lg bg-foreground px-5 py-2.5 text-sm font-medium text-white hover:bg-foreground/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add property
          </Link>
        </div>
      </PortfolioLayout>
    )
  }

  return (
    <PortfolioLayout title="Portfolio" currentUser={currentUser} properties={properties}>
      <div className="space-y-14">
        {/* Section 1: Editorial hero */}
        <section className="animate-slide-in" style={{ animationDelay: '0ms' }}>
          <PortfolioHero
            today={today}
            currentUser={currentUser}
            activeProperties={portfolioKpis!.activeProperties}
            totalRooms={portfolioKpis!.totalRooms}
          />
        </section>

        {/* Section 2: Portfolio KPI strip — newspaper stat row */}
        <section className="animate-slide-in" style={{ animationDelay: '80ms' }}>
          <PortfolioKpiStrip kpis={portfolioKpis!} />
        </section>

        {/* Section 3: Needs attention — anchored so the header bell can scroll here */}
        <section id="needs-attention" className="scroll-mt-24 animate-slide-in" style={{ animationDelay: '160ms' }}>
          <ActionCenter items={actionItems || []} />
        </section>

        {/* Section 4: Property cards */}
        <section className="animate-slide-in" style={{ animationDelay: '240ms' }}>
          <SectionHeader
            eyebrow="The houses"
            title="Tonight at each property"
            description="A 14-day occupancy outlook with vs last year deltas. Click a card to drop into that property."
          />
          <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-3">
            {(propertyCards || []).map((card, idx) => (
              <div key={card.id} className="animate-slide-in" style={{ animationDelay: `${300 + idx * 60}ms` }}>
                <PropertyCard card={card} />
              </div>
            ))}
          </div>
        </section>

        {/* Section 5: Forward 30-day heatmap — the centerpiece insight */}
        <section className="animate-slide-in" style={{ animationDelay: '420ms' }}>
          <SectionHeader
            eyebrow="The forward view"
            title="Next 30 days · occupancy across the portfolio"
            description="Each row is a property, each column a day. Saturate is sold out, sparse is soft. Hover a cell for the breakdown."
          />
          {forwardHeatmap && <ForwardHeatmap data={forwardHeatmap} />}
        </section>

        {/* Section 6: RevPAR small multiples */}
        <section className="animate-slide-in" style={{ animationDelay: '500ms' }}>
          <SectionHeader
            eyebrow="The trend"
            title="RevPAR · last 30 days"
            description="One mini-chart per property, shared scale, so you can read movement at a glance."
          />
          {revparTrends && <RevparSmallMultiples trends={revparTrends} />}
        </section>
      </div>
    </PortfolioLayout>
  )
}

function SectionHeader({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string
  title: string
  description?: string
}) {
  return (
    <div className="mb-5 flex items-end justify-between gap-6">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-primary">{eyebrow}</div>
        <h2 className="mt-1.5 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">{title}</h2>
        {description && (
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p>
        )}
      </div>
    </div>
  )
}
